(() => {
  const isMobile = () => window.matchMedia('(max-width: 1023px)').matches;

  // `options.observeResize` is used by the block version (<custom-media-carousel-block>),
  // whose width can change without the window resizing (e.g. inside a group).
  const initMediaCarousel = (section, options = {}) => {
    if (section.dataset.cmcInitialized === 'true') return;
    section.dataset.cmcInitialized = 'true';

    // Lets destroy() remove every listener added below (used when scheduled slides change).
    const listeners = new AbortController();
    const { signal } = listeners;

    const viewport = section.querySelector('[data-carousel-viewport]');
    const track = section.querySelector('[data-carousel-track]');
    const originalSlides = Array.from(section.querySelectorAll('[data-carousel-slide]:not(.is-clone)'));
    const prev = section.querySelector('[data-carousel-prev]');
    const next = section.querySelector('[data-carousel-next]');
    const dotsWrap = section.querySelector('[data-carousel-dots]');

    if (!viewport || !track || originalSlides.length === 0) return;

    track.querySelectorAll('img, video').forEach((el) => {
      el.setAttribute('draggable', 'false');
    });

    let slides = Array.from(track.children);
    let index = 0;
    let timer = null;
    let marqueeFrame = null;
    let marqueeX = 0;
    let lastFrameTime = null;
    let currentX = 0;
    let isDragging = false;
    let dragStartX = 0;
    let dragStartTranslate = 0;
    let dragMoved = false;
    let suppressClick = false;
    const dragThreshold = 8;
    const dots = [];

    const setVideoMutedState = (video, muted) => {
      video.muted = muted;
      video.defaultMuted = muted;

      if (muted) {
        video.setAttribute('muted', '');
      } else {
        video.removeAttribute('muted');
        video.volume = 1;
      }
    };

    const getSoundToggleVideos = (button) => {
      const wrapper = button.closest('.custom-media-carousel__media-wrapper');
      return wrapper ? Array.from(wrapper.querySelectorAll('video')) : [];
    };

    const updateSoundToggleButton = (button) => {
      const videos = getSoundToggleVideos(button);
      const hasSoundOn = videos.some((video) => !video.muted && video.volume > 0);

      button.classList.toggle('is-sound-on', hasSoundOn);
      button.setAttribute('aria-label', hasSoundOn ? 'Desactivar sonido' : 'Activar sonido');
      button.setAttribute('title', hasSoundOn ? 'Desactivar sonido' : 'Activar sonido');
      button.setAttribute('aria-pressed', hasSoundOn ? 'true' : 'false');
    };

    const syncSoundToggleButtons = () => {
      section.querySelectorAll('[data-cmc-sound-toggle]').forEach(updateSoundToggleButton);
    };

    const initializeVideoSound = (scope = section) => {
      scope.querySelectorAll('video').forEach((video) => {
        video.setAttribute('draggable', 'false');

        if (video.dataset.cmcSoundInitialized === 'true') return;

        video.dataset.cmcSoundInitialized = 'true';
        setVideoMutedState(video, true);

        video.addEventListener('volumechange', syncSoundToggleButtons);
      });

      syncSoundToggleButtons();
    };

    section.addEventListener('click', (event) => {
      const button = event.target.closest('[data-cmc-sound-toggle]');
      if (!button || !section.contains(button)) return;

      event.preventDefault();
      event.stopPropagation();

      const videos = getSoundToggleVideos(button);
      const shouldUnmute = videos.some((video) => video.muted || video.volume === 0);

      videos.forEach((video) => {
        setVideoMutedState(video, !shouldUnmute);

        if (shouldUnmute && !video.paused) {
          const playPromise = video.play?.();

          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {});
          }
        }
      });

      syncSoundToggleButtons();
    }, { signal });

    const getConfig = () => {
      const mobile = isMobile();

      return {
        autoplay: mobile ? section.dataset.autoplayMobile === 'true' : section.dataset.autoplayDesktop === 'true',
        loop: mobile ? section.dataset.loopMobile === 'true' : section.dataset.loopDesktop === 'true',
        center: mobile ? section.dataset.centerMobile === 'true' : section.dataset.centerDesktop === 'true',
        speed: Number(mobile ? section.dataset.speedMobile || 3500 : section.dataset.speedDesktop || 3500),
        initialSlide: Math.max(
          0,
          Number(mobile ? section.dataset.initialSlideMobile || 1 : section.dataset.initialSlideDesktop || 1) - 1
        ),
        marquee: mobile ? section.dataset.marqueeMobile === 'true' : section.dataset.marqueeDesktop === 'true',
        marqueeSpeed: Number(mobile ? section.dataset.marqueeSpeedMobile || 35 : section.dataset.marqueeSpeedDesktop || 45),
        marqueeDirection: section.dataset.marqueeDirection || 'left',
        draggable: mobile ? section.dataset.draggableMobile === 'true' : section.dataset.draggableDesktop === 'true',
        aspectRatio: mobile ? section.dataset.aspectRatioMobile || 'auto' : section.dataset.aspectRatioDesktop || 'auto'
      };
    };

    let config = getConfig();

    const useVariableLayout = () => {
      const mobile = isMobile();

      return mobile
        ? section.dataset.variableLayoutMobile === 'true'
        : section.dataset.variableLayoutDesktop === 'true';
    };

    const shouldUseClones = () => config.loop || config.marquee;

    const getCssNumber = (desktopVar, mobileVar, fallback = 1) => {
      const styles = window.getComputedStyle(section);
      const value = isMobile()
        ? styles.getPropertyValue(mobileVar)
        : styles.getPropertyValue(desktopVar);

      return Math.max(1, Number(value.trim()) || fallback);
    };

    const getRows = () => getCssNumber('--cmc-rows-desktop', '--cmc-rows-mobile', 1);
    const getVisibleColumns = () => getCssNumber('--cmc-visible-desktop', '--cmc-visible-mobile', 1);

    const getGap = () => {
      const styles = window.getComputedStyle(section);
      const value = isMobile()
        ? styles.getPropertyValue('--cmc-gap-mobile')
        : styles.getPropertyValue('--cmc-gap-desktop');

      return Math.max(0, Number.parseFloat(value) || 0);
    };

    const getColumnSpan = (slide) => {
      if (!slide) return 1;

      const styles = window.getComputedStyle(slide);
      const value = isMobile()
        ? styles.getPropertyValue('--cmc-col-span-mobile')
        : styles.getPropertyValue('--cmc-col-span-desktop');

      return Math.max(1, Number.parseInt(value, 10) || 1);
    };

    const getBaseColumnWidth = () => {
      const gap = getGap();
      const referenceSlide = originalSlides.find((slide) => slide.offsetWidth > 0);

      if (referenceSlide) {
        const span = getColumnSpan(referenceSlide);
        const width = (referenceSlide.offsetWidth - gap * (span - 1)) / span;

        if (width > 0) return width;
      }

      const columns = getVisibleColumns();

      return Math.max(
        1,
        (viewport.clientWidth - gap * (columns - 1)) / columns
      );
    };

    const getNaturalMediaAspectRatio = () => {
      for (const slide of originalSlides) {
        const mediaElements = Array.from(slide.querySelectorAll('img, video'));

        for (const media of mediaElements) {
          if (
            media instanceof HTMLImageElement &&
            media.naturalWidth > 0 &&
            media.naturalHeight > 0
          ) {
            return media.naturalWidth / media.naturalHeight;
          }

          if (
            media instanceof HTMLVideoElement &&
            media.videoWidth > 0 &&
            media.videoHeight > 0
          ) {
            return media.videoWidth / media.videoHeight;
          }
        }
      }

      return 1;
    };

    const getBaseAspectRatio = () => {
      if (config.aspectRatio !== 'auto') {
        const [width, height] = config.aspectRatio.split('/').map(Number);

        if (width > 0 && height > 0) {
          return width / height;
        }
      }

      return getNaturalMediaAspectRatio();
    };

    const updateVariableRowHeight = () => {
      if (!useVariableLayout()) {
        section.style.removeProperty('--cmc-variable-row-height');
        return;
      }

      const columnWidth = getBaseColumnWidth();
      const aspectRatio = Math.max(0.01, getBaseAspectRatio());

      section.style.setProperty(
        '--cmc-variable-row-height',
        `${columnWidth / aspectRatio}px`
      );
    };

    const getOrderedOriginalSlides = () => {
      if (useVariableLayout()) return originalSlides;

      const rows = getRows();
      const columns = getVisibleColumns();
      const pageSize = rows * columns;

      if (rows <= 1) return originalSlides;

      const ordered = [];

      for (
        let pageStart = 0;
        pageStart < originalSlides.length;
        pageStart += pageSize
      ) {
        const page = originalSlides.slice(
          pageStart,
          pageStart + pageSize
        );

        for (let column = 0; column < columns; column++) {
          for (let row = 0; row < rows; row++) {
            const logicalIndex = row * columns + column;

            if (page[logicalIndex]) {
              ordered.push(page[logicalIndex]);
            }
          }
        }
      }

      return ordered;
    };

    const getLogicalIndexFromDomOriginalIndex = (domOriginalIndex) => {
      const ordered = getOrderedOriginalSlides();
      const slide = ordered[domOriginalIndex];

      return Math.max(0, originalSlides.indexOf(slide));
    };

    const getDomOriginalIndexFromLogicalIndex = (logicalIndex) => {
      const ordered = getOrderedOriginalSlides();
      const slide = originalSlides[logicalIndex];

      return Math.max(0, ordered.indexOf(slide));
    };

    const reorderOriginalSlides = () => {
      const ordered = getOrderedOriginalSlides();

      ordered.forEach((slide) => {
        track.appendChild(slide);
      });

      slides = Array.from(track.children);
    };

    const removeClones = () => {
      track.querySelectorAll('.is-clone').forEach((clone) => clone.remove());
      reorderOriginalSlides();
      slides = Array.from(track.children);
    };

    const createClones = () => {
      removeClones();

      const ordered = getOrderedOriginalSlides();

      ordered.forEach((slide) => {
        const clone = slide.cloneNode(true);

        clone.classList.add('is-clone');
        clone.removeAttribute('data-carousel-slide');
        clone.setAttribute('aria-hidden', 'true');

        clone.querySelectorAll('img, video').forEach((el) => {
          el.setAttribute('draggable', 'false');
        });

        initializeVideoSound(clone);
        track.appendChild(clone);
      });

      [...ordered].reverse().forEach((slide) => {
        const clone = slide.cloneNode(true);

        clone.classList.add('is-clone');
        clone.removeAttribute('data-carousel-slide');
        clone.setAttribute('aria-hidden', 'true');

        clone.querySelectorAll('img, video').forEach((el) => {
          el.setAttribute('draggable', 'false');
        });

        initializeVideoSound(clone);
        track.insertBefore(clone, track.firstChild);
      });

      slides = Array.from(track.children);
    };

    const syncSlides = () => {
      if (shouldUseClones()) {
        createClones();
      } else {
        removeClones();
      }
    };

    const getRealIndex = () => {
      const total = originalSlides.length;

      if (!shouldUseClones()) {
        return getLogicalIndexFromDomOriginalIndex(index);
      }

      const domOriginalIndex =
        ((index - total) % total + total) % total;

      return getLogicalIndexFromDomOriginalIndex(domOriginalIndex);
    };

    const getSlideX = (slide) => {
      if (!slide) return 0;

      if (config.center) {
        return slide.offsetLeft -
          (viewport.clientWidth - slide.offsetWidth) / 2;
      }

      return slide.offsetLeft;
    };

    const getTargetX = () => getSlideX(slides[index]);

    const setTranslate = (x, animate = true) => {
      currentX = x;

      track.style.transition =
        animate && !config.marquee && !isDragging
          ? 'transform 600ms ease'
          : 'none';

      track.style.transform = `translate3d(${-x}px, 0, 0)`;
    };

    const updateDots = () => {
      const realIndex = getRealIndex();

      dots.forEach((dot, dotIndex) => {
        dot.classList.toggle(
          'is-active',
          dotIndex === realIndex
        );
      });
    };

    const updateSlides = () => {
      const realIndex = getRealIndex();

      originalSlides.forEach((slide, slideIndex) => {
        slide.classList.toggle(
          'is-active',
          slideIndex === realIndex
        );
      });
    };

    const updateVideos = () => {
      const realIndex = getRealIndex();

      originalSlides.forEach((slide, slideIndex) => {
        slide.querySelectorAll('video').forEach((video) => {
          if (slideIndex === realIndex) {
            video.play?.().catch(() => {});
          }
        });
      });
    };

    const getIndexFromLogicalIndex = (logicalIndex) => {
      const domOriginalIndex =
        getDomOriginalIndexFromLogicalIndex(logicalIndex);

      return shouldUseClones()
        ? originalSlides.length + domOriginalIndex
        : domOriginalIndex;
    };

    const jumpToEquivalentMiddleSlide = () => {
      if (!shouldUseClones()) return;

      const realIndex = getRealIndex();

      index = getIndexFromLogicalIndex(realIndex);
      setTranslate(getTargetX(), false);
    };

    const update = (animate = true) => {
      setTranslate(getTargetX(), animate);
      updateDots();
      updateSlides();
      updateVideos();
    };

    const goTo = (nextIndex) => {
      if (!shouldUseClones()) {
        nextIndex = Math.max(
          0,
          Math.min(nextIndex, originalSlides.length - 1)
        );
      }

      index = nextIndex;
      update(true);
    };

    const stopAutoplay = () => {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    };

    const startAutoplay = () => {
      if (
        config.marquee ||
        !config.autoplay ||
        originalSlides.length < 2
      ) {
        return;
      }

      stopAutoplay();

      timer = window.setInterval(() => {
        goTo(index + 1);
      }, config.speed);
    };

    const stopMarquee = () => {
      if (marqueeFrame) {
        window.cancelAnimationFrame(marqueeFrame);
        marqueeFrame = null;
      }

      lastFrameTime = null;
    };

    const getOriginalSetWidth = () => {
      const ordered = getOrderedOriginalSlides();

      if (ordered.length === 0) return 0;

      const firstOriginal = ordered[0];
      const lastOriginal = ordered[ordered.length - 1];

      return (
        lastOriginal.offsetLeft +
        lastOriginal.offsetWidth -
        firstOriginal.offsetLeft
      );
    };

    const startMarquee = () => {
      if (!config.marquee || originalSlides.length < 2) return;

      section.classList.add('is-marquee');

      stopAutoplay();
      stopMarquee();

      if (!track.querySelector('.is-clone')) {
        createClones();
      }

      const ordered = getOrderedOriginalSlides();
      const middleStart = ordered[0].offsetLeft;
      const setWidth = getOriginalSetWidth();

      if (!setWidth) return;

      marqueeX = middleStart;

      const step = (time) => {
        if (!lastFrameTime) {
          lastFrameTime = time;
        }

        const delta = (time - lastFrameTime) / 1000;

        lastFrameTime = time;

        const direction =
          config.marqueeDirection === 'right' ? -1 : 1;

        marqueeX += config.marqueeSpeed * delta * direction;

        if (marqueeX >= middleStart + setWidth) {
          marqueeX -= setWidth;
        }

        if (marqueeX < middleStart) {
          marqueeX += setWidth;
        }

        setTranslate(marqueeX, false);

        marqueeFrame =
          window.requestAnimationFrame(step);
      };

      marqueeFrame =
        window.requestAnimationFrame(step);
    };

    const restart = () => {
      stopAutoplay();
      stopMarquee();

      const previousRealIndex = getRealIndex();

      config = getConfig();

      section.classList.toggle(
        'is-marquee',
        config.marquee
      );

      syncSlides();
      updateVariableRowHeight();

      index =
        getIndexFromLogicalIndex(previousRealIndex);

      if (config.marquee) {
        startMarquee();
      } else {
        update(false);
        startAutoplay();
      }
    };

    const buildDots = () => {
      if (!dotsWrap) return;

      dotsWrap.innerHTML = '';
      dots.length = 0;

      originalSlides.forEach((_, slideIndex) => {
        const dot = document.createElement('button');

        dot.type = 'button';
        dot.className = 'custom-media-carousel__dot';

        dot.setAttribute(
          'aria-label',
          `Go to slide ${slideIndex + 1}`
        );

        dot.addEventListener('click', () => {
          index =
            getIndexFromLogicalIndex(slideIndex);

          update(true);
          restart();
        }, { signal });

        dotsWrap.appendChild(dot);
        dots.push(dot);
      });
    };

    const getClosestSlideIndex = (x) => {
      let closestIndex = index;
      let closestDistance = Infinity;

      slides.forEach((slide, slideIndex) => {
        const slideX = getSlideX(slide);
        const distance = Math.abs(slideX - x);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = slideIndex;
        }
      });

      return closestIndex;
    };

    const onPointerDown = (event) => {
      if (!config.draggable) return;

      if (
        event.button !== undefined &&
        event.button !== 0
      ) {
        return;
      }

      isDragging = true;
      dragMoved = false;
      suppressClick = false;
      dragStartX = event.clientX;
      dragStartTranslate = currentX;

      stopAutoplay();
      stopMarquee();
    };

    const onPointerMove = (event) => {
      if (!isDragging) return;

      const delta =
        event.clientX - dragStartX;

      if (Math.abs(delta) > dragThreshold) {
        if (!dragMoved) {
          dragMoved = true;
          suppressClick = true;

          section.classList.add('is-dragging');

          viewport.setPointerCapture?.(
            event.pointerId
          );
        }

        event.preventDefault();

        setTranslate(
          dragStartTranslate - delta,
          false
        );
      }
    };

    const onPointerUp = (event) => {
      if (!isDragging) return;

      const wasDragged = dragMoved;

      isDragging = false;

      section.classList.remove('is-dragging');

      if (wasDragged) {
        viewport.releasePointerCapture?.(
          event.pointerId
        );

        event.preventDefault();
        event.stopPropagation();

        index =
          getClosestSlideIndex(currentX);

        update(true);
        restart();

        window.setTimeout(() => {
          suppressClick = false;
          dragMoved = false;
        }, 50);
      }
    };

    viewport.addEventListener(
      'dragstart',
      (event) => {
        if (config.draggable) {
          event.preventDefault();
        }
      },
      { signal }
    );

    viewport.addEventListener(
      'pointerdown',
      onPointerDown,
      { signal }
    );

    viewport.addEventListener(
      'pointermove',
      onPointerMove,
      { signal }
    );

    viewport.addEventListener(
      'pointerup',
      onPointerUp,
      { signal }
    );

    viewport.addEventListener(
      'pointercancel',
      onPointerUp,
      { signal }
    );

    viewport.addEventListener(
      'click',
      (event) => {
        if (suppressClick) {
          event.preventDefault();
          event.stopPropagation();

          suppressClick = false;
          dragMoved = false;
        }
      },
      { capture: true, signal }
    );

    initializeVideoSound();
    syncSlides();
    updateVariableRowHeight();
    buildDots();

    index = getIndexFromLogicalIndex(
      Math.min(
        config.initialSlide,
        originalSlides.length - 1
      )
    );

    originalSlides.forEach((slide) => {
      slide.querySelectorAll('img').forEach((image) => {
        if (!image.complete) {
          image.addEventListener(
            'load',
            () => {
              updateVariableRowHeight();
              update(false);
            },
            { once: true, signal }
          );
        }
      });

      slide.querySelectorAll('video').forEach((video) => {
        if (video.readyState < 1) {
          video.addEventListener(
            'loadedmetadata',
            () => {
              updateVariableRowHeight();
              update(false);
            },
            { once: true, signal }
          );
        }
      });
    });

    prev?.addEventListener('click', () => {
      if (config.marquee) {
        config.marqueeDirection = 'right';
        section.dataset.marqueeDirection = 'right';

        restart();
        return;
      }

      goTo(index - 1);
      restart();
    }, { signal });

    next?.addEventListener('click', () => {
      if (config.marquee) {
        config.marqueeDirection = 'left';
        section.dataset.marqueeDirection = 'left';

        restart();
        return;
      }

      goTo(index + 1);
      restart();
    }, { signal });

    track.addEventListener(
      'transitionend',
      () => {
        if (config.marquee || !config.loop) {
          return;
        }

        if (
          index >= originalSlides.length * 2 ||
          index < originalSlides.length
        ) {
          jumpToEquivalentMiddleSlide();
        }
      },
      { signal }
    );

    section.addEventListener(
      'mouseenter',
      () => {
        stopAutoplay();

        if (config.marquee) {
          stopMarquee();
        }
      },
      { signal }
    );

    section.addEventListener(
      'mouseleave',
      () => {
        if (config.marquee) {
          startMarquee();
        } else {
          startAutoplay();
        }
      },
      { signal }
    );

    const onWindowResize = () => {
      restart();
    };

    window.addEventListener(
      'resize',
      onWindowResize,
      { signal }
    );

    let resizeObserver = null;
    let resizeFrame = null;

    if (options.observeResize && 'ResizeObserver' in window) {
      let lastWidth = Math.round(section.getBoundingClientRect().width);

      resizeObserver = new ResizeObserver((entries) => {
        const width = Math.round(entries[0].contentRect.width);

        if (width === lastWidth) return;

        lastWidth = width;

        window.cancelAnimationFrame(resizeFrame);
        resizeFrame = window.requestAnimationFrame(() => {
          if (!section.isConnected) return;

          restart();
        });
      });

      resizeObserver.observe(section);
    }

    const initialFrame = requestAnimationFrame(() => {
      updateVariableRowHeight();
      update(false);

      if (config.marquee) {
        startMarquee();
      } else {
        startAutoplay();
      }
    });

    const pause = () => {
      window.cancelAnimationFrame(initialFrame);
      window.cancelAnimationFrame(resizeFrame);
      stopAutoplay();
      stopMarquee();
      window.removeEventListener('resize', onWindowResize);
      resizeObserver?.disconnect();
    };

    return {
      pause,
      // Leaves the markup as it was before initialisation (original slides only).
      destroy: () => {
        pause();
        listeners.abort();
        track.querySelectorAll('.is-clone').forEach((clone) => clone.remove());
        track.style.removeProperty('transform');
        track.style.removeProperty('transition');
        if (dotsWrap) dotsWrap.innerHTML = '';
        section.classList.remove('is-marquee', 'is-dragging');
        section.style.removeProperty('--cmc-variable-row-height');
        delete section.dataset.cmcInitialized;
      },
      resume: () => {
        window.removeEventListener('resize', onWindowResize);
        window.addEventListener('resize', onWindowResize);
        resizeObserver?.observe(section);
        restart();
      }
    };
  };

  /**
   * Slides can be scheduled (settings rendered by snippets/custom-schedule.liquid, logic in
   * window.CustomSchedule). Carousels without scheduled slides, and every carousel in the
   * theme editor, are initialised exactly as before. Otherwise only the active slides are
   * kept in the track and the carousel is rebuilt whenever a slide starts or ends.
   */
  const initScheduledMediaCarousel = (section, options = {}) => {
    const schedule = window.CustomSchedule;
    const track = section.querySelector('[data-carousel-track]');
    const slides = track
      ? Array.from(track.querySelectorAll(':scope > [data-carousel-slide]:not(.is-clone)'))
      : [];

    if (!schedule || schedule.designMode || !slides.some((slide) => slide.hasAttribute('data-schedule'))) {
      return initMediaCarousel(section, options);
    }

    let controller = null;
    let timer = null;

    const apply = () => {
      window.clearTimeout(timer);

      const now = Date.now();
      const active = slides.filter((slide) => schedule.isActive(slide, now));

      slides.forEach((slide) => {
        if (active.includes(slide)) return;
        schedule.stopMedia(slide);
        slide.remove();
      });

      // Keep the original order, only moving slides that are not already in place.
      active.forEach((slide, slideIndex) => {
        slide.style.removeProperty('display');
        schedule.restoreMedia(slide);
        const current = track.children[slideIndex] || null;
        if (current !== slide) track.insertBefore(slide, current);
      });

      if (active.length) {
        section.style.removeProperty('display');
        controller = initMediaCarousel(section, options) || null;
      } else {
        section.style.setProperty('display', 'none', 'important');
        controller = null;
      }

      const next = schedule.getNextChange(slides, now);
      if (next !== null) timer = window.setTimeout(rebuild, schedule.getDelay(next));
    };

    const rebuild = () => {
      controller?.destroy();
      controller = null;
      apply();
    };

    apply();

    return {
      pause: () => {
        window.clearTimeout(timer);
        controller?.pause();
      },
      // Rebuilt instead of resumed, in case a slide started or ended while paused.
      resume: rebuild,
      destroy: () => {
        window.clearTimeout(timer);
        controller?.destroy();
      },
    };
  };

  const initAll = () => {
    document
      .querySelectorAll('[data-custom-media-carousel]')
      .forEach((section) => initScheduledMediaCarousel(section));
  };

  document.addEventListener(
    'DOMContentLoaded',
    initAll
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      const section = event.target.querySelector(
        '[data-custom-media-carousel]'
      );

      if (section) {
        initScheduledMediaCarousel(section);
      }
    }
  );

  // Block version: initialises itself when it enters the DOM (page load, theme editor
  // re-renders) and pauses when removed. It does not use [data-custom-media-carousel],
  // so the section initialisation above never touches it.
  if (!customElements.get('custom-media-carousel-block')) {
    customElements.define(
      'custom-media-carousel-block',
      class extends HTMLElement {
        connectedCallback() {
          if (this.cmcController) {
            this.cmcController.resume();
            return;
          }

          if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
          } else {
            this.init();
          }
        }

        disconnectedCallback() {
          this.cmcController?.pause();
        }

        init() {
          if (!this.isConnected || this.cmcController) return;

          this.cmcController = initScheduledMediaCarousel(this, { observeResize: true });
        }
      }
    );
  }
})();